import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connectInfiniteHits } from "react-instantsearch-dom";
import { PropTypes } from "prop-types";
import isEqual from "lodash/isEqual";

import { useTracking } from "@context/Tracking";
import { useProductData2 } from "@context/products";
import { useLocalizedSentenceDict } from "@hooks/useSentenceDict";
import useIntersectionObserver from "@hooks/useIntersectionObserver";
import useDataByHandles from "@hooks/useDataByHandles";

import { ComponentType } from "@constants/ComponentType";
import ProductCollection from "@components/CollectionPage/ProductCollection";

const convertHitToProduct = (p, hts) =>
  // turn it into the shape that ProductCollection expects
  ({
    node: { ...p.shopify, shopifyId: p.shopify.id },
    contentful: {
      node: {
        ...p.contentful,
        hts,
      },
    },
    variant: p.forSearch.variant,
    productPath: p.forSearch.productPath,
  });

function HitToProductCollection({
  hit,
  position,
  indexName,
  queryId,
  productCardClick,
  hts,
  alternativesData = [],
  bundleProducts = [],
}) {
  const product = useMemo(() => {
    const base = convertHitToProduct(hit, hts);
    return {
      ...base,
      contentful: {
        node: {
          ...base.contentful.node,
          bundleProducts,
        },
      },
    };
  }, [hit, hts, bundleProducts]);

  const alternativesDataFiltered = alternativesData.filter((p) => p);
  // Retrieve product data for each alternative
  const alternativesProducts = useProductData2(
    alternativesDataFiltered.map(({ handle }) => handle),
    "long"
  );
  // Enrich product with alternatives and products data of each alternative in a similar manner as in PLPs
  const productWithAlternatives = useMemo(() => {
    if (!alternativesDataFiltered?.length || !alternativesProducts.data) return product;
    const alternativesProductsByHandle = alternativesProducts.data.reduce(
      (acc, alternativesProduct) => {
        acc[alternativesProduct.handle] = alternativesProduct;
        return acc;
      },
      {}
    );
    const productAlternatives = {
      alternatives: alternativesDataFiltered.map((altData) => ({
        ...altData,
        ...(alternativesProductsByHandle[altData.handle] || {}),
      })),
    };
    productAlternatives.alternatives.forEach((alt) => {
      if (alt.contentful?.node) {
        alt.contentful.node.productAlternatives = productAlternatives;
      }
    });
    return {
      ...product,
      contentful: {
        node: {
          ...product.contentful.node,
          productAlternatives,
        },
      },
    };
  }, [product, alternativesDataFiltered, alternativesProducts.data]);

  // searchObject is used to track ATC on PDP after a search
  const searchObject = useMemo(
    () => ({
      isSearch: true,
      index: indexName,
      queryID: queryId,
      objectID: hit.objectID,
    }),
    [indexName, queryId, hit.objectID]
  );

  // Keep ATC hidden until bundle products are available
  const shouldHideATC =
    product.node.tags.includes("bundle") &&
    bundleProducts.length === 0 &&
    !product.node.tags.includes("Components:Solid Gold");

  return (
    <ProductCollection
      key={`search-product-${hit.objectID}`}
      product={productWithAlternatives}
      variantId={productWithAlternatives.variant?.id}
      className="product_collection"
      onClick={() => productCardClick(hit.objectID, position + 1)}
      searchObject={searchObject}
      componentType={ComponentType.SEARCH}
      cpPosition={position + 1}
      hideQuickAdd={shouldHideATC}
    />
  );
}

function CustomInfiniteHits({ hits, hasMore, refineNext, indexName, queryId }) {
  const loadMoreDetectorRef = useRef(null);
  const dict = useLocalizedSentenceDict();
  const { trackProductClickAfterSearch } = useTracking();
  const [hitHandles, setHitHandles] = useState((hits || []).map((item) => item.handle));

  // Whenever hits change, update hitHandles
  // hits change very often, even though it has the same value
  // so only set hitHandles when the value is different
  useEffect(() => {
    const newHitHandles = hits.map((item) => item.handle);
    if (!isEqual(newHitHandles.sort(), hitHandles.sort())) {
      setHitHandles(newHitHandles);
    }
  }, [hits, hitHandles]);

  const htsData = useDataByHandles("product-hts", {}, hitHandles);
  const alternativesData = useDataByHandles("product-alternatives", {}, hitHandles);
  const bundlesData = useDataByHandles("product-bundles", {}, hitHandles);

  useIntersectionObserver({
    target: loadMoreDetectorRef,
    onIntersect: () => {
      refineNext();
    },
    enabled: hasMore,
  });

  const productCardClick = useCallback(
    (objectID, position) => {
      trackProductClickAfterSearch(indexName, queryId, objectID, position);
    },
    [indexName, queryId, trackProductClickAfterSearch]
  );

  return (
    <div className="grid grid-cols-4 gap-x-[15px] gap-y-[25px] mobile:grid-cols-2 mobile:gap-x-[15px] mobile:gap-y-[30px] max-w-[1140px] mx-auto mobile:px-[15px] pb-[60px]">
      {hits.map((hit, position) => (
        <HitToProductCollection
          key={hit.objectID}
          hit={hit}
          position={position}
          indexName={indexName}
          queryId={queryId}
          productCardClick={productCardClick}
          hts={htsData?.data?.find((item) => item?.handle && item.handle === hit.handle)?.hts}
          alternativesData={
            alternativesData?.data?.find(
              (altData) => altData?.handle && altData.handle === hit.handle
            )?.productAlternatives
          }
          bundleProducts={bundlesData?.data?.find((b) => b?.handle === hit.handle)?.bundleProducts}
        />
      ))}

      <div className="text-center" ref={loadMoreDetectorRef}>
        {!hasMore && hits.length > 0 && <div>{dict.get("No more results")}</div>}
      </div>
    </div>
  );
}

CustomInfiniteHits.propTypes = {
  hits: PropTypes.array,
  hasMore: PropTypes.bool,
  refineNext: PropTypes.func,
  indexName: PropTypes.string,
  queryId: PropTypes.string,
};

export default connectInfiniteHits(CustomInfiniteHits);
